/** @jsx jsx */
// HOC
import { jsx } from '@emotion/react';

import * as React from 'react';

import useRouter, { useLocation } from 'js/lib/useRouter';
import user from 'js/lib/user';
import withUrlSessionStore from 'js/lib/withUrlSessionStore';

import { Grid, Hidden, PageGridContainer, useTheme } from '@coursera/cds-core';
// Components
import { Container } from '@coursera/coursera-ui';

import PageHeader from 'bundles/page-header/components/PageHeader';
import { CourseraMetatags } from 'bundles/seo';
import TeamsCheckoutProgressBar from 'bundles/teams-v2/components/TeamsCheckoutProgressBar';
import { TeamsContext } from 'bundles/teams-v2/components/TeamsContainer';
import TeamsPurchaseSummary from 'bundles/teams-v2/components/TeamsPurchaseSummary';
import TeamsValueItem from 'bundles/teams-v2/components/TeamsValueItem';
import AffiliateLogo from 'bundles/teams-v2/components/shared/AffiliateLogo';
import { hasPromoMockBanners } from 'bundles/teams-v2/utils/withPromotionBanners';

import 'css!./__styles__/TeamsMainApp';

export type CheckoutSessionType = {
  businessEmail: string;
  fullName: string;
  alreadyC4TUserEmail?: string;
};

type Props = {};

export const TeamsMainApp: React.FC<Props> = ({ children }) => {
  const router = useRouter();
  const { pathname, query } = useLocation();
  const path = (pathname && pathname.split('/').pop()) || '';
  const { enterprisePromotion, promoCode, freeTrial } = React.useContext(TeamsContext);
  const hasMockPromotion = !freeTrial && hasPromoMockBanners({ promotionId: enterprisePromotion?.id });
  const wrapperClass = 'rc-TeamsMainApp theme-light';

  const theme = useTheme();

  if (path === 'confirmation' || path === 'freetrial-confirmation') {
    return (
      <div className={wrapperClass}>
        <div className="c-centerApp">
          <Container>{children}</Container>
        </div>
      </div>
    );
  }

  return (
    <div className="rc-TeamsV2App">
      <CourseraMetatags metaNameAndProperties={{ disableCrawlerIndexing: true }} />

      <PageHeader
        shouldSkipOptionalExternalDataFetch={true}
        hideSearch={true}
        hideEnterprise={true}
        showShoppingCart={true}
        hideMetaNav
        affiliateElement={
          // Redundent check for TS
          hasMockPromotion && enterprisePromotion?.id ? <AffiliateLogo promotionId={enterprisePromotion.id} /> : null
        }
        showEnterpriseLogo={true}
        hideRightNav={!user.isAuthenticatedUser()}
        logoQueryParams={{ promoCode }}
        preventModalClose
        preventRegister
        showLanguagesDropdown={false}
      />
      <PageGridContainer css={{ maxWidth: 1084, marginTop: theme.spacing(24) }}>
        <Grid item xs={12} sm={8}>
          <TeamsCheckoutProgressBar router={router} path={path} />
          <Hidden smUp>
            <TeamsPurchaseSummary query={query} mobileOnly={true} css={{ marginBottom: theme.spacing(24) }} />
          </Hidden>
          <div>{children}</div>
          <Hidden smUp>
            <TeamsValueItem />
          </Hidden>
        </Grid>
        <Grid item sm={4}>
          <Hidden xsDown>
            <TeamsPurchaseSummary path={path} query={query} mobileOnly={false} />
            <TeamsValueItem />
          </Hidden>
        </Grid>
      </PageGridContainer>
    </div>
  );
};

export default withUrlSessionStore()(TeamsMainApp);
